import React, { useState, useEffect } from 'react';
import SEOHead from '../components/SEOHead';
import { SITE_INFO } from '../data/siteData';
import ContactForm from '../components/ContactForm';
import { fetchKontaktSide, FALLBACK_KONTAKT_SIDE } from '../lib/cms';

export default function ContactPage() {
  const [kontaktData, setKontaktData] = useState(FALLBACK_KONTAKT_SIDE);

  useEffect(() => {
    async function load() {
      const data = await fetchKontaktSide();
      if (data) setKontaktData(data);
    }
    load();
  }, []);

  const phone = kontaktData.telefon || SITE_INFO.phone;
  const email = kontaktData.email || SITE_INFO.email;
  const address = kontaktData.adresse || `${SITE_INFO.address}, ${SITE_INFO.zipCity}`;
  const fbUrl = kontaktData.facebook_url || SITE_INFO.facebookUrl;
  const fbText = kontaktData.facebook_tekst || "Følg KerneViden på Facebook";
  return (
    <div className="bg-white min-h-screen">
      <SEOHead
        title="Kontakt :: KerneViden"
        description="Der tilbydes en indledende telefonisk/online samtale gratis."
        ogTitle="Kontakt :: KerneViden"
        ogDescription="Der tilbydes en indledende telefonisk/online samtale gratis."
      />

      {/* Main Content */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {/* Left Col: Contact Info & Practical Details */}
            <div className="lg:col-span-5 space-y-8">
              <div className="space-y-6">
                <div>
                  <h2 className="font-serif text-2xl sm:text-3xl text-[#233424] font-medium tracking-tight">
                    {kontaktData.overskrift || "Kontakt på telefon eller mail."}
                  </h2>
                  <p className="text-sm sm:text-base text-[#5B6A5A] mt-2">
                    {kontaktData.undertekst || "Jeg bestræber mig på at svare hurtigt og altid med nærvær og respekt for din situation."}
                  </p>
                </div>

                <div className="space-y-6 pt-2">
                  {/* Phone */}
                  <div className="flex items-start gap-3.5">
                    <i className="fa-solid fa-phone text-base text-[#233424] mt-0.5 shrink-0" aria-hidden="true" />
                    <div>
                      <span className="text-xs text-[#758473] block font-medium">Telefon</span>
                      <a
                        href={`tel:${phone.replace(/\s+/g, '')}`}
                        className="text-base font-semibold text-[#233424] hover:text-[#3A4F3D] transition-colors whitespace-nowrap"
                      >
                        <span className="whitespace-nowrap">{phone}</span>
                      </a>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex items-start gap-3.5">
                    <i className="fa-solid fa-envelope text-base text-[#233424] mt-0.5 shrink-0" aria-hidden="true" />
                    <div>
                      <span className="text-xs text-[#758473] block font-medium">Email</span>
                      <a
                        href={`mailto:${email}`}
                        className="text-sm sm:text-base font-semibold text-[#233424] hover:text-[#3A4F3D] transition-colors whitespace-nowrap block"
                      >
                        <span className="whitespace-nowrap">{email}</span>
                      </a>
                    </div>
                  </div>

                  {/* Address */}
                  <div className="flex items-start gap-3.5">
                    <i className="fa-solid fa-location-dot text-base text-[#233424] mt-0.5 shrink-0" aria-hidden="true" />
                    <div>
                      <span className="text-xs text-[#758473] block font-medium">Adresse</span>
                      <p className="text-sm font-semibold text-[#233424]">
                        {address}
                      </p>
                    </div>
                  </div>

                  {/* Facebook */}
                  {fbUrl && (
                    <div className="flex items-start gap-3.5">
                      <i className="fa-brands fa-facebook text-lg text-[#233424] mt-0.5 shrink-0" aria-hidden="true" />
                      <div>
                        <span className="text-xs text-[#758473] block font-medium">Sociale medier</span>
                        <a
                          href={fbUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-semibold text-[#233424] hover:text-[#3A4F3D] transition-colors inline-flex items-center gap-1.5"
                        >
                          <span>{fbText}</span>
                          <i className="fa-solid fa-arrow-up-right-from-square text-xs" aria-hidden="true" />
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Col: Webform */}
            <div className="lg:col-span-7">
              <ContactForm delay={0} />
            </div>

          </div>
        </div>
      </section>

      {/* Google Maps Section in Black & White at the bottom */}
      <section className="bg-white pb-16 sm:pb-20 pt-4" aria-label="Kort over lokation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <div>
              <h3 className="font-serif text-xl sm:text-2xl text-[#233424] font-medium tracking-tight">
                Find vej til samtalerummet
              </h3>
              <p className="text-xs sm:text-sm text-[#758473] mt-0.5">
                Farimagsvej 26, 4700 Næstved
              </p>
            </div>
            <a
              href="https://maps.google.com/?cid=10285821931261714405"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-medium text-[#233424] hover:text-[#3A4F3D] transition-colors underline underline-offset-4"
            >
              <span>Åbn i Google Maps</span>
              <i className="fa-solid fa-arrow-up-right-from-square text-xs" aria-hidden="true" />
            </a>
          </div>

          {/* Grayscale Map Container */}
          <div className="w-full h-80 sm:h-96 rounded-2xl overflow-hidden shadow-[0_12px_36px_rgba(44,62,45,0.06)] border border-[#E7DFD4] relative">
            <iframe
              title="KerneViden adresse Farimagsvej 26 Næstved"
              src="https://maps.google.com/maps?q=Farimagsvej%2026%2C%204700%20N%C3%A6stved&t=&z=15&ie=UTF8&iwloc=&output=embed"
              className="w-full h-full border-0 grayscale contrast-125 brightness-95"
              loading="lazy"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
